<template>
  <v-card>
    <v-layout row>
      <v-spacer/>
      <v-flex
        xs6
        md2
        pa-3
      >
        <v-select
          v-model="activityFilter"
          :items="activeSelect"
          :label="$t('activity')"
          item-text="name"
          item-value="value"
        />
      </v-flex>
      <v-flex
        xs6
        md4
        pa-3
      >
        <v-text-field
          :label="$t('searchEmployee')"
          v-model="search"
          append-icon="search"
        />
      </v-flex>
    </v-layout>

    <v-data-table
      :headers="headers"
      :items="employees"
      v-model="selected"
      :loading="isLoading"
      :pagination.sync="pagination"
      :total-items="paginationTotal"
      :rows-per-page-items="[15, 25, 50]"
      must-sort
      select-all
      class="text-xs-left"

    >
      <template
        slot="items"
        slot-scope="props">
        <tr
          :key="props.index"
          @click="navigateToEmployee(props.item.id)"
        >
          <td>
            <v-checkbox
              :input-value="props.selected"
              primary
              hide-details
              @click.stop="props.selected = !props.selected"/>
          </td>
          <td
            v-for="(field, index) in fieldsRequired"
            :key="index">
            {{ props.item[field] }}
          </td>
          <td>
            <span v-if="props.item.latest_timesheet">
              {{ $moment(props.item.latest_timesheet.month, 'MM-YYYY').format('MM.YYYY') }}
            </span>
          </td>
          <td>
            <v-chip
              v-if="props.item.is_currently_inactive"
              color="red"
              text-color="white">{{ $t('currently_inactive') }}
            </v-chip>
            <v-chip v-if="props.item.is_part_time_worker">{{ $t('is_part_time_worker') }}</v-chip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-card-actions>
      <v-spacer/>
      <v-btn
        v-show="isCompanyExist && $can('create', 'Employee')"
        color="primary"
        class="ml-2"
        @click.native="$router.push({ name: 'employeeCreate' })">
        {{ $t('newEmployee') }}
      </v-btn>
      <timesheet-batch-one-month-multiple-employees
        v-if="selected.length"
        :employees="selected"/>
    </v-card-actions>
  </v-card>
</template>

<script>
import EmployeeCreate from './EmployeeCreate'
import CompanyApi from '@/api/CompanyApi'
import PaginationApi from '@/api/PaginationApi'
import TimesheetBatchOneMonthMultipleEmployees from './TimesheetBatchOneMonthMultipleEmployees'
import debounce from 'lodash/debounce'
import syncDataWithLocalStorageMixin from '../../../mixins/syncDataWithLocalStorageMixin'
import { mapGetters } from 'vuex'

export default {
  name: 'EmployeesOverview',
  components: { TimesheetBatchOneMonthMultipleEmployees, EmployeeCreate },
  mixins: [syncDataWithLocalStorageMixin],
  props: ['companyId'],
  data () {
    return {
      activityFilter: 'all',
      employees: [],
      isCompanyExist: false,
      dialog: false,
      selected: [],
      pagination: {},
      activeSelect: [
        {
          name: this.$t('all'),
          value: 'all'
        },
        {
          name: this.$t('active'),
          value: 'false'
        },
        {
          name: this.$t('inactive'),
          value: 'true'
        }
      ],
      paginationTotal: null,
      isLoading: false,
      search: null,
      fieldsRequired: [
        'number',
        'first_name',
        'last_name'
      ],
      headers: [
        { text: this.$t('number'), value: 'number', align: 'left', index: '0', sortable: true },
        { text: this.$t('firstName'), value: 'first_name', align: 'left', index: '1', sortable: true },
        { text: this.$t('lastName'), value: 'last_name', align: 'left', index: '2', sortable: true },
        { text: this.$t('latestTimesheet'), value: 'latest_timesheet', align: 'left', index: '3', sortable: false },
        { text: '', value: 'is_part_time_worker', align: 'left', index: '4', sortable: false }
      ]
    }
  },
  computed: {
    url () {
      return 'company/employees'
    },
    ...mapGetters('user', ['getUser'])
  },

  methods: {
    searchEmployee: debounce(async function (e) {
      if (e === '') {
        this.getPaginatedData()
      }

      if (e && e.length) {
        this.getPaginatedData()
      }
    }, 250),

    navigateToEmployee (employeeId) {
      this.$router.push({ name: 'employee', params: { employeeId } })
    },
    async getPaginatedData () {
      this.isLoading = true
      try {
        const paginationParams = []
        const query = {}

        if (this.search) {
          paginationParams.push(`search=${this.search}`)
          query.search = this.search
        }

        paginationParams.push(`page=${this.pagination.page}`)
        query.page = this.pagination.page

        if (this.activityFilter !== 'all') {
          paginationParams.push(`filterBy=is_currently_inactive:${this.activityFilter}`)
          query.filterBy = `is_currently_inactive:${this.activityFilter}`
        }

        paginationParams.push(`rowsPerPage=${this.pagination.rowsPerPage}`)
        query.rowsPerPage = this.pagination.rowsPerPage

        paginationParams.push(
          `sortBy=${this.pagination.sortBy}`,
          `descending=${this.pagination.descending}`
        )
        query.sortBy = this.pagination.sortBy
        query.descending = this.pagination.descending

        this.$router.push({ name: this.$route.name, query })
        const response = await PaginationApi.getPaginatedData(this.url, paginationParams)

        this.paginationTotal = response.total
        this.employees = response.data

        const { sortBy, rowsPerPage, descending } = query
        this.$storeData({ sortBy, rowsPerPage, descending })
      } catch (e) {
        if (this.getUser.has_company) {
          this.$notification(e)
        }
      }
      this.isLoading = false
    }
  },
  watch: {
    pagination: {
      deep: true,
      handler: 'getPaginatedData'
    },
    activityFilter: 'getPaginatedData',
    search: 'searchEmployee'
  },

  i18n: {
    messages: {
      en: {
        firstName: 'First name',
        lastName: 'Last name',
        newEmployee: 'Create employee',
        number: '#',
        searchEmployee: 'Search employee',
        is_part_time_worker: 'Without fixed work hours',
        currently_inactive: 'Currently inactive',
        latestTimesheet: 'Latest document',
        active: 'Active',
        inactive: 'Inactive',
        all: 'All',
        activity: 'Activity'
      },
      tr: {
        firstName: 'İsim',
        heading: 'Çalışan bilgileri',
        lastName: 'Soyisim',
        newEmployee: 'Çalışan tanımla',
        number: '#',
        searchEmployee: 'Arama',
        is_part_time_worker: 'Sabit çalışma saatleri olmadan',
        currently_inactive: 'Etkisiz',
        latestTimesheet: 'son belge',
        active: 'Aktif',
        inactive: 'Etkisiz',
        all: 'Tüm',
        activity: 'Etkinlik'
      },
      de: {
        firstName: 'Vorname',
        lastName: 'Nachname',
        newEmployee: 'Mitarbeiter erstellen',
        number: '#',
        searchEmployee: 'Mitarbeiter suchen',
        is_part_time_worker: 'Ohne feste Arbeitszeiten',
        currently_inactive: 'Aktuell inaktiv',
        latestTimesheet: 'Letztes Dokument',
        active: 'Aktiv',
        inactive: 'Inaktiv',
        all: 'Alle',
        activity: 'Aktivität'
      },
      ru: {
        firstName: 'Имя',
        lastName: 'Фамилия',
        newEmployee: 'Создать сотрудника',
        number: '#',
        searchEmployee: 'Поиск сотрудника',
        is_part_time_worker: 'Без фиксированного рабочего времени',
        currently_inactive: 'неактивный',
        latestTimesheet: 'последний документ',
        active: 'Активные',
        inactive: 'Неактивные',
        all: 'Все',
        activity: 'Активность'
      }
    }
  },
  async created () {
    if (this.$route.query) {
      const userId = this.$store.state.user.userInfo.id
      this.localStorageSyncConfigKey = `elsazeit.config.${userId}.employeesOverview`
      this.$loadData()

      if (!this.$syncDataToLocalStorage) this.$storeData(this.$route.query)
      const { sortBy, rowsPerPage, descending } = this.$syncDataToLocalStorage
      const { search, page } = this.$route.query
      this.search = search
      this.pagination.sortBy = sortBy || 'number'
      this.pagination.descending = String(descending) === 'true'
      this.pagination.page = Number(page) || 1
      this.pagination.rowsPerPage = Number(rowsPerPage) || 15
    }

    if (this.$can('read', 'AllEmployees')) return

    try {
      await CompanyApi.getCompany() // cm should have created company in order to create employees
      this.isCompanyExist = true
    } catch (e) {
      this.isCompanyExist = false
    }
  }
}
</script>

<style scoped>
  .inactive-employee {
    color: grey;
  }
</style>
